/**
 * Schreibwege im Vertrieb nach Rechten (Rollensystem, docs/rollensystem-plan.md
 * §11, Paket C4).
 *
 * Ob jemand eine Ansicht (Kunden, Neukunden, Karte, Angebote) überhaupt sieht,
 * entscheidet die Wache in App.tsx (`services/rechte/ansichtRechte.ts`). Hier
 * steht, was INNERHALB einer Ansicht angeboten wird — die Kundenakte erreicht
 * zum Beispiel auch, wer nur Angebote schreibt, ändern darf er dort nichts.
 *
 * Die Datenbank (Nr. 152) zieht dieselben Grenzen: `customers` anlegen und
 * die Kundennummer nur mit „Kunden verwalten“, Zusammenführen ebenso; Leads,
 * Lead-Scout und Ausschreibungsfunde = „Neukunden / Leads“; Angebote,
 * Vorlagen, Textbausteine und Ausschreibungsunterlagen = „Angebote“. Ändern
 * darf `customers` dort auch „Angebote“ — die Annahme als Rahmenvertrag
 * schreibt den Vertrag in den Kundenstamm. Die Oberfläche bietet das Ändern
 * des Kundenstamms trotzdem nur mit „Kunden verwalten“ an (weniger als die
 * Datenbank, nie mehr); Löschen gibt es über Rechte gar nicht.
 *
 * Rein, ohne React und ohne Supabase — `useVertriebRechte` reicht den Stand
 * herein.
 */
import type { AppView } from '../../types';
import type { RechtSchluessel } from '../../services/rechte/katalog';
import { ansichtErlaubt, type AnsichtRechteSicht } from '../../services/rechte/ansichtRechte';
import { anlageTypVon, anlegeRecht, darfIrgendeinProjektAnlegen } from '../../services/projektAnlageRechte';

export interface VertriebRechte {
  /** Kunden anlegen, ändern, archivieren, zusammenführen, importieren, verorten. */
  kunden: boolean;
  /** Leads anlegen und bearbeiten, Lead-Import, Abgleich. */
  leads: boolean;
  /** Angebote, Vorlagen, Textbausteine, Ausschreibungen, Annahme. */
  angebote: boolean;
  /**
   * Führt ein Sprung in diese Ansicht weiter (Kundenakte, Angebot, Rechnung)?
   * Dieselbe Regel wie die Wache in App.tsx — ein Link, der auf „Kein
   * Zugriff“ führt, wird gar nicht erst angeboten.
   */
  ansicht: (view: AppView) => boolean;
}

const OFFEN: VertriebRechte = { kunden: true, leads: true, angebote: true, ansicht: () => true };

/** Die Schreibwege für eine Systemrolle und ihre Rechte (`useRechte`). */
export const vertriebRechte = (
  rechte: AnsichtRechteSicht,
  systemrolle: string | null | undefined,
): VertriebRechte => ({
  kunden: rechte.hat('kunden.verwalten'),
  leads: rechte.hat('leads.verwalten'),
  angebote: rechte.hat('angebote.verwalten'),
  ansicht: (view) => ansichtErlaubt(view, rechte, systemrolle, { istSifaFachkraft: false }),
});

/**
 * Die Schreibwege in einer Ansicht HINTER der Wache, die die Systemrolle
 * nicht kennt (App reicht sie den Vertriebsansichten nicht durch).
 *
 * Maßgeblich ist dann die Antwort der Datenbank (`meine_rechte()`): Admins
 * bekommen den ganzen Katalog, Freelancer und Gesperrte nichts, alle anderen
 * die Rechte ihrer Rollen. Fehlt die Antwort (Migration offen oder noch
 * unterwegs), hat die Wache nur Admins durchgelassen — die Systemrolle
 * `user`/`arbeitsvorbereitung` hat ohne Migration kein Vertriebsrecht. Dann
 * bleibt alles wie vor dem Rollensystem: jeder Weg offen, die Datenbank
 * entscheidet.
 */
export const vertriebRechteHinterWache = (
  dbRechte: ReadonlySet<RechtSchluessel> | null,
): VertriebRechte => {
  if (!dbRechte) return OFFEN;
  return vertriebRechte({ hat: (r) => dbRechte.has(r) }, null);
};

export interface KundenBearbeitungImAngebot {
  /** Stift am Kundennamen zeigen? */
  offen: boolean;
  /** Reichweite „Kundenstamm“ wählbar — schreibt `customers`. */
  stamm: boolean;
}

/**
 * Kundendaten aus einem Angebot heraus bearbeiten.
 *
 * Zwei Reichweiten mit zwei Rechten: der Kundenstamm (`customers`) braucht
 * „Kunden verwalten“; die Abweichung nur auf diesem Angebot
 * (`details.customerOverride`) schreibt das Angebot und braucht „Angebote“ —
 * aber nur, solange das Angebot sie noch aufnimmt (Entwurf,
 * `canScopeToDocument`). Ein versendetes Angebot druckt seinen Schnappschuss;
 * wer dort nur Angebote schreiben darf, hätte nichts zu bearbeiten.
 */
export const kundenBearbeitungImAngebot = (
  rechte: Pick<VertriebRechte, 'kunden' | 'angebote'>,
  hatKunde: boolean,
  belegbezogenMoeglich: boolean,
): KundenBearbeitungImAngebot => {
  const stamm = hatKunde && rechte.kunden;
  const beleg = hatKunde && rechte.angebote && belegbezogenMoeglich;
  return { offen: stamm || beleg, stamm };
};

/**
 * Sperrt ein fehlendes Recht „Angenommen → Auftrag“? Die Annahme legt ein
 * Projekt des im Dialog gewählten Typs an (ab Nr. 154 lehnt die Datenbank die
 * Anlage ohne `projekte.<typ>_anlegen` ab). Der Knopf ist offen, sobald
 * irgendein Typ angelegt werden darf — der Dialog bietet dann nur diese
 * Typen an (`erlaubteProjektTypen`). Ein Angebot ohne Endsumme lässt sich
 * auch als Rahmenvertrag annehmen — ohne Projekt; dann bleibt der Knopf
 * ebenfalls offen, und erst die Projektanlage im Dialog fragt.
 * `null` = frei, sonst das fehlende Recht (für den Hinweis am Knopf: das für
 * SiGeKo, den häufigsten Fall).
 */
export const annahmeGesperrtDurch = (
  rechte: AnsichtRechteSicht,
  rahmenvertragMoeglich: boolean,
): RechtSchluessel | null => {
  if (darfIrgendeinProjektAnlegen(rechte.hat) || rahmenvertragMoeglich) return null;
  return anlegeRecht(anlageTypVon(undefined));
};
